import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { IEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { IPropertyBoundControl, PropertyBoundControl, PropertyBoundControlPropertyValues } from "./PropertyBoundControl";
import { NAMESPACE } from "./_Namespace";

import "./InputControl.scss";

export type InputControlPropertyValues =
    PropertyBoundControlPropertyValues
    & {
        errorState: boolean;
        mandatory: boolean;
        mandatoryEvaluator: IEvaluator;
        readOnly: boolean;
    };

export type InputControlProperties = {
    [name in keyof InputControlPropertyValues]: Property<InputControlPropertyValues[name]>;
};

export interface IInputControl<TPropertyValues extends InputControlPropertyValues = InputControlPropertyValues> extends IPropertyBoundControl, InputControlProperties
{
}

export abstract class InputControl<TPropertyValues extends InputControlPropertyValues = InputControlPropertyValues> extends PropertyBoundControl<TPropertyValues> implements IInputControl<TPropertyValues>
{
    // #region Static

    public static override classNamespace = NAMESPACE + "InputControl";
    public static override className = "InputControl";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<InputControlPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                errorState: false,
                mandatory: false,
                readOnly: false,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected __processElementValueChanged: boolean;
    public __updatingDataSource: boolean;

    // #endregion

    // #region Properties

    public errorState!: PropertyBoolean;
    public mandatory!: PropertyBoolean;
    public mandatoryEvaluator!: Property<IEvaluator>;
    public readOnly!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.__processElementValueChanged = true;
        this.__updatingDataSource = false;

        this.addCssClass("rad-control-input-control");

        // Trigger Property Changes
        this.errorState.triggerChange();
        this.mandatory.triggerChange();
        this.mandatoryEvaluator.triggerChange();
        this.readOnly.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public isMandatoryGet()
    {
        return this.visible.get() && (this.mandatory.get() || this.mandatoryEvaluator.get()?.value.get());
    }
    public updateDataSource()
    {
        if (this.__refreshingFromDataSource)
            return;

        if (this.__updatingDataSource)
            return;

        this._suspendRefreshFromDataSource();
        this.__updatingDataSource = true;
        this._updateDataSource();
        this.__updatingDataSource = false;
        this._resumeRefreshFromDataSource();
    }
    public updateMandatory()
    {
        if (this.isMandatoryGet())
            this.addCssClass("rad-state-mandatory");
        else
            this.removeCssClass("rad-state-mandatory");
    }

    // #endregion

    // #region Protected Methods

    protected _errorStateChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-error");
        else
            this.removeCssClass("rad-state-error");
    }
    protected _mandatoryChanged(change: PropertyChange<boolean>)
    {
        this.updateMandatory();
    }
    protected _mandatoryEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        if (change.originalValue)
        {
            change.originalValue.off("valueChanged", { self: this }, this.onMandatoryEvaluatorValueChanged);
            change.originalValue.context.set(null);
        }

        if (change.currentValue)
        {
            change.currentValue.on("valueChanged", { self: this }, this.onMandatoryEvaluatorValueChanged);
            change.currentValue.context.set(this);
        }
    }
    protected _readOnlyChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    public _updateDataSource()
    {
        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        const
            dataSourceProperty = this.dataSourceProperty.get();

        if (!dataSourceProperty)
            return;

        const
            dataSourcePropertyType = this.dataSourcePropertyType.get();

        let
            value = this.value.get();

        if (dataSourcePropertyType)
            value = ObjectHelper.asDotNetType(value, dataSourcePropertyType);

        dataSource.propertyValueByPathSet(dataSourceProperty, value);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.errorState = new PropertyBoolean(this, propertyValues.errorState, this._errorStateChanged);
        this.mandatory = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.mandatory,
            changeHandler: this._mandatoryChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Mandatory" }),
            ],
        });
        this.mandatoryEvaluator = new Property<IEvaluator>({
            context: this,
            dotNetType: "System.Object, System.Private.CoreLib",
            initialValue: propertyValues.mandatoryEvaluator,
            changeHandler: this._mandatoryEvaluatorChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Mandatory Evaluator" }),
                // propertyEditorClass: "PropertyEditorEvaluatorTree"
            ],
        });
        this.readOnly = new PropertyBoolean(this, propertyValues.readOnly, this._readOnlyChanged);
    }

    protected override _dispose()
    {
        const
            mandatoryEvaluator = this.mandatoryEvaluator.get();

        if (mandatoryEvaluator)
            mandatoryEvaluator.off("valueChanged", { self: this }, this.onMandatoryEvaluatorValueChanged);

        super._dispose();
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        this.updateDataSource();
        super._valueChanged(change);
    }

    public override updateVisibility()
    {
        super.updateVisibility();
        this.updateMandatory();
    }

    // #endregion

    // #region Event Handlers

    protected onMandatoryEvaluatorValueChanged(evt: IJNativeEvent, change: PropertyChange<any>)
    {
        const
            self = evt.data.self as this;

        self.updateMandatory();
    }

    // #endregion

    // #endregion
}